import React from "react";
import MaskInput, { MaskedInputProps } from "react-maskedinput";
import styled, { css, theme, ThemeProp } from "../../styled";
import withMargin, { WithMarginProp } from "../../styled/withMargin";
import { Text } from "./Text";
import Icon, { IconComponent } from "../../components/Icon";
import useDimensions from "../../hooks/useDimensions";
import { isNotNullOrUndefined } from "../../utils/types";
import { isServer } from "../../global";

const OuterWrapper = styled.div<WithMarginProp>`
  ${withMargin};
`;

const InputWrapper = styled.div`
  position: relative;
`;

const inputBorderWidth = "2px";
const basePadding = "12px";
const iconButtonWidth = "24px";
const iconButtonSpaceAsPadding = `${basePadding} + ${iconButtonWidth} + ${theme.spacing.s}`;

interface LayoutProps {
  hasLabel?: boolean;
  hasValue?: boolean;
  hasError?: boolean;
  hasLeadingIcon?: boolean;
  hasTrailingIcon?: boolean;
}

export const commonTextInputStyles = (p: ThemeProp & LayoutProps) => css`
  width: 100%;

  border: ${inputBorderWidth} solid
    ${p.hasError ? p.theme.color.red60 : p.theme.color.gray20};
  border-radius: ${p.theme.misc.borderRadius};
  padding: calc(${basePadding} - ${inputBorderWidth}) ${basePadding};
  font-size: ${p.theme.typography.size.m};
  font-family: ${p.theme.typography.bodyFamily};
  line-height: ${p.theme.typography.lineHeight.m};
  background-color: ${p.theme.color.white};
  color: ${p.theme.color.gray90};
  outline: none;
  resize: vertical;
  -webkit-appearance: none;

  &:not(:disabled):not(:focus):hover {
    border-color: ${p.hasError ? p.theme.color.red80 : p.theme.color.gray40};
  }

  &:focus {
    border-color: ${p.hasError ? p.theme.color.red60 : p.theme.color.blue60};
    box-shadow: ${`${p.theme.shadow.focusRing} ${
      p.hasError ? p.theme.color.red20 : p.theme.color.blue20
    }`};
  }
`;

const inputStyles = (
  p: ThemeProp &
    LayoutProps & { labelHeight: number; as?: "input" | "textarea" }
) => css`
  ${commonTextInputStyles}


  &::placeholder {
    color: ${p.theme.color.gray40};
  }

  &:disabled {
    background-color: ${p.theme.color.gray10};
    border-color: ${p.theme.color.gray10};
    cursor: not-allowed;
  }

  ${p.hasLabel &&
    css`
      padding-top: ${`calc(${p.theme.spacing.xs} + ${p.labelHeight}px + ${inputBorderWidth})`};
      padding-bottom: ${`calc(${p.theme.spacing.s} - ${inputBorderWidth})`};
    `}

  ${p.hasLeadingIcon &&
    css`
      padding-left: ${`calc(${iconButtonSpaceAsPadding})`};
    `}

  ${p.hasTrailingIcon &&
    css`
      padding-right: ${`calc(${iconButtonSpaceAsPadding})`};
    `}

  ${p.as === "textarea" &&
    css`
      min-height: ${`calc(100px + ${p.labelHeight}px + ${inputBorderWidth})`};
    `}
`;

const StyledInput = styled.input<
  { labelHeight: number; as?: "input" | "textarea" } & LayoutProps
>`
  ${inputStyles}
`;

const StyledMaskInput = styled(
  ({
    // Remove the layout helper props from being rendered in HTML
    hasLabel,
    hasValue,
    hasError,
    hasLeadingIcon,
    hasTrailingIcon,
    labelHeight,
    ...props
  }) => <MaskInput {...props} />
)<
  React.InputHTMLAttributes<HTMLInputElement> &
    LayoutProps & { labelHeight: number }
>`
  ${inputStyles}
`;

const Label = styled.label<LayoutProps & { isDisabled?: boolean }>`
  display: block;
  position: absolute;
  top: ${p => p.theme.spacing.s};
  left: ${p => `calc(${p.theme.spacing.s} + ${p.theme.spacing.xs} + 2px)`};
  right: ${p => `calc(${p.theme.spacing.s} + ${p.theme.spacing.xs} + 2px)`};
  font-size: ${p => p.theme.typography.size.m};
  line-height: ${p => p.theme.typography.lineHeight.m};
  color: ${p => p.theme.color.gray70};
  cursor: ${p => (p.isDisabled ? "not-allowed" : "pointer")};

  /*
  * Truncate the label content for first-pass
  * server side render before we know the height
  * of the label
  */
  ${() => {
    if (isServer()) {
      return css`
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `;
    }
  }}

  ${StyledInput}:focus + & {
    color: ${p => (p.hasError ? p.theme.color.red70 : p.theme.color.blue70)};
  }

  ${p => {
    if (p.hasError && !p.isDisabled) {
      return css`
        color: ${p.theme.color.red70};
      `;
    }
    if (p.hasValue && !p.isDisabled) {
      return css`
        color: ${p.theme.color.blue70};
      `;
    }
  }}

  ${p => {
    if (p.hasLeadingIcon) {
      return css`
        left: ${`calc(${iconButtonSpaceAsPadding} + 2px)`};
      `;
    }
  }}

  ${p => {
    if (p.hasTrailingIcon) {
      return css`
        right: ${`calc(${iconButtonSpaceAsPadding} + 2px)`};
      `;
    }
  }}
`;

const LabelFade = styled.div<{ labelHeight: number }>`
  /*
   * Fade to transparent, WebKit doesn't support using "transparent"
   * in a linear gradient here, so we have to use rgba instead
   * https://stackoverflow.com/a/56548711
   */
  background: linear-gradient(
    to bottom,
    ${p => p.theme.color.white} 80%,
    rgba(255, 255, 255, 0)
  );

  display: block;
  position: absolute;
  border-radius: ${p => p.theme.misc.borderRadius};
  top: ${inputBorderWidth};
  left: ${inputBorderWidth};
  right: calc(${inputBorderWidth} + ${p => p.theme.spacing.m});
  min-height: ${p => `calc(${p.labelHeight}px + ${p.theme.spacing.m})`};
  padding-bottom: ${p => p.theme.spacing.s};
`;

interface IconWrapperProps extends LayoutProps {
  variant: "leading" | "trailing";
}

const IconWrapper = styled.div<IconWrapperProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  ${p => {
    if (p.variant === "leading") {
      return css`
        left: ${`calc(${p.theme.spacing.s} + ${inputBorderWidth})`};
      `;
    }

    if (p.variant === "trailing") {
      return css`
        right: ${`calc(${p.theme.spacing.s} + ${inputBorderWidth})`};
      `;
    }
  }}
`;

const IconButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${p => p.theme.spacing.xs};
  border-radius: ${p => p.theme.misc.borderRadius};

  &:focus {
    outline: none;
    box-shadow: ${p => `${p.theme.shadow.focusRing} ${p.theme.color.blue20}`};
  }

  &:not(div):hover svg {
    color: ${p => p.theme.color.blue50};
    fill: ${p => p.theme.color.blue50};
  }
`;

const AdditionalMessage = styled(Text)`
  padding: ${p => `${p.theme.spacing.s} 12px 0`};
`;

interface IconProp
  extends React.PropsWithoutRef<JSX.IntrinsicElements["button"]> {
  icon: IconComponent;
}

interface BaseTextInputProps
  extends WithMarginProp,
    Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      "onChange" | "onBlur" | "value"
    > {
  id?: string;
  value?: string;
  name: string;
  label?: string;
  errorMessage?: string;
  helpMessage?: string | React.ReactNode;
  isDisabled?: boolean;
  placeholder?: string;
  iconLeading?: IconProp;
  iconTrailing?: IconProp;
  onChange: (event: React.FormEvent<HTMLInputElement>, value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export interface AsInputProps extends BaseTextInputProps {
  as?: "input";
  maskOptions?: MaskedInputProps;
  nonSensitive?: boolean;
}

export interface AsTextAreaProps extends BaseTextInputProps {
  as: "textarea";
  nonSensitive?: boolean;
}

export type TextInputProps = AsTextAreaProps | AsInputProps;

export const TextInput = ({
  label,
  name,
  type = "text",
  isDisabled,
  errorMessage,
  helpMessage,
  iconLeading,
  iconTrailing,
  margin,
  className,
  as = "input",
  nonSensitive = false,
  ...props
}: TextInputProps) => {
  const layoutHelpers = {
    hasLabel: Boolean(label),
    hasValue: Boolean(props.value),
    hasError: Boolean(errorMessage),
    hasLeadingIcon: Boolean(iconLeading),
    hasTrailingIcon: Boolean(iconTrailing)
  };

  const [labelElement, dimensions] = useDimensions<HTMLLabelElement>();

  const minLabelHeight = layoutHelpers.hasLabel ? 24 : 0;

  const labelHeight = isNotNullOrUndefined(dimensions.height)
    ? dimensions.height
    : minLabelHeight;

  const labelledBy = !!label ? `${name}-label` : undefined;

  const describedBy =
    helpMessage && !errorMessage
      ? `${name}-help-message`
      : `${name}-error-message`;

  return (
    <OuterWrapper margin={margin} className={className}>
      <InputWrapper>
        {"maskOptions" in props && Boolean(props.maskOptions) ? (
          <StyledMaskInput
            {...props}
            {...layoutHelpers}
            {...props.maskOptions}
            labelHeight={labelHeight}
            type={type}
            name={name}
            id={name}
            disabled={isDisabled}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              props.onChange(e, e.currentTarget.value);
            }}
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            className={!nonSensitive ? "fs-exclude" : ""}
          />
        ) : (
          <StyledInput
            {...props}
            {...layoutHelpers}
            labelHeight={labelHeight}
            type={type}
            name={name}
            id={name}
            as={as}
            disabled={isDisabled}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              props.onChange(e, e.currentTarget.value);
            }}
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            className={nonSensitive ? "fs-exclude" : ""}
            {...(as === "textarea" ? { rows: 3 } : undefined)}
          />
        )}

        {as === "textarea" && label && <LabelFade labelHeight={labelHeight} />}

        {label && (
          <Label
            ref={labelElement}
            id={labelledBy}
            htmlFor={name}
            {...layoutHelpers}
            isDisabled={isDisabled}
          >
            {label}
          </Label>
        )}

        {iconLeading && (
          <IconWrapper variant="leading" {...layoutHelpers}>
            <IconButton
              as={!Boolean(iconLeading.onClick) ? "div" : "button"}
              {...iconLeading}
            >
              <Icon type={iconLeading.icon} />
            </IconButton>
          </IconWrapper>
        )}

        {iconTrailing && (
          <IconWrapper variant="trailing" {...layoutHelpers}>
            <IconButton
              as={!Boolean(iconTrailing.onClick) ? "div" : "button"}
              {...iconTrailing}
            >
              <Icon type={iconTrailing.icon} />
            </IconButton>
          </IconWrapper>
        )}
      </InputWrapper>
      {/* For aria-live to work properly the element with the attribute
       * needs to be included in the initial markup for the page, so we
       * always have this div in the DOM and conditionally populate
       * it with the additional message with padding applied
       */}
      <div id={`${name}-error-message`} aria-live="polite">
        {errorMessage && (
          <AdditionalMessage color="red70" size="s">
            {errorMessage}
          </AdditionalMessage>
        )}
      </div>

      <div id={`${name}-help-message`} aria-live="polite">
        {!errorMessage && helpMessage && (
          <AdditionalMessage color="gray70" size="s">
            {helpMessage}
          </AdditionalMessage>
        )}
      </div>
    </OuterWrapper>
  );
};
